import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Icon,
  type ITabItem,
  SpinnerLoader,
  Tabs,
  Tooltip,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { checkAccessPage, EnumAccessPageKeys } from 'entities/ExportRouters/lib/checkAccessPage'
import { useInboxesViewContext } from 'pages/settings/pages/inboxes/context/inboxesViewContext'
import { GroupInboxesTab } from 'pages/settings/pages/inboxes/pages/inboxesView/ui/InboxesTable/GroupInboxesTab/GroupInboxesTab'
import { InboxesTab } from 'pages/settings/pages/inboxes/pages/inboxesView/ui/InboxesTable/AllInboxesTab/InboxesTab'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import styles from './styles.module.scss'

const tabs: ITabItem[] = [
  {
    name: 'All inboxes',
    key: 'all',
  },
  {
    name: 'Group inboxes',
    key: 'group',
  },
]

export const InboxesView = observer(() => {
  const {
    inboxesViewStore,
    groupInboxesViewStore,
    isManageOrganizationInboxes,
    manageInboxWrapper,
    init,
  } = useInboxesViewContext()
  const { firstLoading, entities, items, showAccessModal } = inboxesViewStore
  const { groupInboxFormStore } = groupInboxesViewStore
  const isTwilioNumberVendor = entities.organizationStore.isTwilioNumberVendor
  const [activeTab, setActiveTab] = useState(tabs[0].key)
  const isAvailableInboxes = groupInboxFormStore.availableInboxes.length

  useEffect(() => {
    if (items.length && !isManageOrganizationInboxes) {
      showAccessModal()
    }
  }, [items.length, isManageOrganizationInboxes])

  useEffect(() => {
    void init()
  }, [])

  if (firstLoading) {
    return <SpinnerLoader ariaLabel={'InboxesView'} />
  }

  const addInbox = () =>
    manageInboxWrapper(() =>
      uiStore.changeRouteViaVue({ path: `/${InboxesPath.addInbox.stepsRoot}` })
    )

  if (!items.length && isTwilioNumberVendor) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        height='100%'
        gap={2}
      >
        <Icon color='var(--content-primary-tertiary)' icon='inboxes2' />
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          gap={0.5}
        >
          <Typography variant='body-rg-medium' ariaLabel='NoAccessTitle'>
            Let’s create your first inbox
          </Typography>
          <div className={styles.createInboxText}>
            <Typography variant='body-md-regular' ariaLabel='NoAccessText' color='tertiary'>
              To start texting with your Twilio number, <br /> you’ll need to create an inbox.
            </Typography>
          </div>
        </Box>
        <Button text='Create inbox' iconProps={{ icon: 'add' }} size='medium' onClick={addInbox} />
      </Box>
    )
  }

  if (!items.length) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        height='100%'
        gap={2}
      >
        <Icon color='var(--content-primary-tertiary)' icon='inboxes2' />
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          gap={0.5}
        >
          <Typography variant='body-rg-medium' ariaLabel='NoAccessTitle'>
            You don’t have access to any inboxes
          </Typography>
          <Typography variant='body-md-regular' ariaLabel='NoAccessText' color='tertiary'>
            Only inbox owners can view or manage them.
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <div className={styles.content}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='flex-end'
        padding='64px 20px 0'
      >
        <div className={styles.inboxesDetailsHeader}>
          <Typography variant='heading-lg' ariaLabel='Inboxes_title'>
            Inboxes
          </Typography>
          <Typography variant='body-md-regular' ariaLabel='Numbers_subtitle_text' color='tertiary'>
            Manage all inboxes in your workspace
          </Typography>
        </div>
        {checkAccessPage(EnumAccessPageKeys.create_inbox, entities) && (
          <Box display='flex' gap={1}>
            <Tooltip
              label={!isAvailableInboxes ? 'You have added all inboxes' : ''}
              placement={'top'}
              visibility={!isAvailableInboxes ? 'visible' : 'hidden'}
            >
              <Button
                text='Create group inbox'
                contained='secondary'
                iconProps={{ icon: 'add' }}
                size='medium'
                onClick={() => groupInboxFormStore.handleGroupInboxModal()}
                disabled={!isAvailableInboxes}
              />
            </Tooltip>
            <Button text='New inbox' iconProps={{ icon: 'add' }} size='medium' onClick={addInbox} />
          </Box>
        )}
      </Box>
      <div className={styles.navBar}>
        <Tabs
          tabs={tabs}
          activeTabKey={activeTab}
          handleSelectTab={(tab) => setActiveTab(tab.key)}
          tabsClassName={styles.navBarTabs}
        />
        {activeTab === 'all' && <InboxesTab />}
        {activeTab === 'group' && <GroupInboxesTab />}
      </div>
    </div>
  )
})
